import { ApplicationRef, computed, inject, Injectable, isDevMode, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { concat, EMPTY, first, interval, map } from 'rxjs';

const UPDATE_CHECK_INTERVAL_MS = 1 * 60 * 60 * 1000;

// Dev-only global hook so `SwUpdate.checkForUpdate()` can be triggered manually
// from the browser console on an already-open tab, without waiting for the poll.
declare global {
  interface Window {
    __checkForUpdate?: () => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AppUpgrade {
  private readonly sw = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);

  // Rising-edge flag: `versionUpdates` is a hot, non-replaying stream, so a VERSION_READY
  // event that fired before we subscribed would be missed. `checkForUpdate()` re-detects
  // it on demand instead (periodic poll or dev console hook). Never reset to `false`:
  // once an update is confirmed, it stays available for the rest of the app/tab lifetime.
  private readonly checkForUpdateFoundUpdate = signal(false);

  private readonly versionReady: Signal<boolean> = toSignal(
    this.sw.isEnabled ? this.sw.versionUpdates.pipe(map((event) => event.type === 'VERSION_READY')) : EMPTY,
    { initialValue: false },
  );

  public readonly updateAvailable: Signal<boolean> = computed(
    () => this.versionReady() || this.checkForUpdateFoundUpdate(),
  );

  public constructor() {
    this.pollForUpdates();

    if (isDevMode()) {
      this.registerDevUpdateCheckHook();
    }
  }

  public update(): void {
    void this.sw.activateUpdate().then(() => document.location.reload());
  }

  private pollForUpdates(): void {
    if (!this.sw.isEnabled) {
      return;
    }

    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    const everyInterval$ = interval(UPDATE_CHECK_INTERVAL_MS);

    concat(appIsStable$, everyInterval$).subscribe(() => {
      void this.sw
        .checkForUpdate()
        .then((updateFound) => {
          if (updateFound) {
            this.checkForUpdateFoundUpdate.set(true);
          }
        })
        .catch(() => {
          // Swallow errors (e.g. offline) so polling continues on the next interval.
        });
    });
  }

  private registerDevUpdateCheckHook(): void {
    window.__checkForUpdate = () => {
      if (!this.sw.isEnabled) {
        console.log('Service worker disabled');
        return;
      }

      void this.sw.checkForUpdate().then((updateFound) => {
        console.log(`Update found: ${updateFound}`);
        if (updateFound) {
          this.checkForUpdateFoundUpdate.set(true);
        }
      });
    };
  }
}
